import { useCookieInteraction, usePageLoadedTracking } from "@jmc/core/src/hooks";
import { EventTypes } from "@jmc/core/src/types/EventTypes";
import { useOnScreen } from "@jmc/utils/hooks/useOnScreen";
import React, { ReactNode, useEffect, useRef } from "react";

export interface BaseImpressionProps {
    children: ReactNode;
    eventType: EventTypes;
    trackingInfo: Record<string, unknown>;
    trackingOptions?: Record<string, unknown>;
    delay?: number;
    additionalConditions?: boolean[];
    onTrack?: () => void;
    checkVisibility?: boolean;
    containerRef?: React.RefObject<HTMLDivElement>;
    containerProps?: React.HTMLAttributes<HTMLDivElement>;
}

/**
 * 🏛️ This component is used to track impressions of elements on the page when they want to be tracked on display.
 */
export const BaseImpression: React.FC<BaseImpressionProps> = ({
    children,
    eventType,
    trackingInfo,
    trackingOptions = {},
    delay = 0,
    additionalConditions = [],
    onTrack,
    checkVisibility = false,
    containerRef,
    containerProps = {},
    ...rest
}) => {
    const { interacted } = useCookieInteraction();
    const internalRef = useRef<HTMLDivElement>(null);
    const ref = containerRef || internalRef;
    const isVisible = useOnScreen(ref);

    const { shouldTrack, executeTracking } = usePageLoadedTracking({
        delay,
        conditions: [...(checkVisibility ? [isVisible] : []), interacted, ...additionalConditions],
        onTrack,
    });

    useEffect(() => {
        if (shouldTrack) {
            executeTracking(eventType, {
                ...trackingOptions,
            });
        }
    }, [shouldTrack, eventType]);

    return (
        <div
            data-tracking-event={eventType}
            data-tracking-info={JSON.stringify(trackingInfo)}
            ref={ref}
            {...containerProps}
            {...rest}
        >
            {children}
        </div>
    );
};
